import { Link } from 'react-router-dom';
import { ArrowRight, Camera } from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { Button } from '@/components/ui';
import { ScreenFrame } from '@/components/ProductTour';
import { SCREENS } from '@/lib/screens';
import type { PublicConfig } from '@shared/types';

/**
 * Every screen, at full size.
 *
 * The landing page shows a handful; somebody who has got as far as this page
 * wants to see the thing itself, so nothing is held back and each shot keeps
 * its own sentence explaining what it is looking at.
 *
 * The note at the bottom about the pictures being real is not marketing — it
 * is the reason the capture pipeline exists, and it is the kind of claim a
 * visitor is right to be sceptical about.
 */
export default function Tour() {
  const { data: pub } = useApi<PublicConfig>('/public/config');
  const canSignUp = pub?.signup_enabled !== false;

  const desktop = SCREENS.filter((s) => s.shape === 'desktop');
  const phone = SCREENS.filter((s) => s.shape === 'phone');

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 space-y-16">
      <header className="max-w-2xl">
        <p className="label mb-4">A look inside</p>
        <h1 className="text-display">What PROVIA actually looks like</h1>
        <p className="text-lg text-muted mt-5 leading-relaxed">
          These are photographs of the running app, not drawings of it. The portfolio
          in them is invented — a small Leeds landlord with six rooms, some rent paid
          and some not.
        </p>
      </header>

      <div className="space-y-16">
        {desktop.map((screen, i) => (
          <section key={screen.id} className="space-y-5">
            <div className="max-w-2xl">
              <h2 className="text-title">{screen.title}</h2>
              <p className="text-muted mt-2 leading-relaxed">{screen.caption}</p>
            </div>
            <ScreenFrame screen={screen} priority={i === 0} />
          </section>
        ))}
      </div>

      {phone.length > 0 && (
        <section className="space-y-8">
          <div className="max-w-2xl">
            <h2 className="text-title">The tenant's side</h2>
            <p className="text-muted mt-2 leading-relaxed">
              Tenants sign in with their phone number and a code — no password to
              forget. They see what they owe, pay with proof, upload what you need
              and message you.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-10">
            {phone.map((screen) => (
              <div key={screen.id} className="space-y-4">
                <ScreenFrame screen={screen} />
                <div className="text-center max-w-xs mx-auto">
                  <p className="font-semibold">{screen.title}</p>
                  <p className="text-sm text-muted mt-1 leading-relaxed">{screen.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="panel sm:p-8">
        <div className="flex items-start gap-3">
          <Camera className="h-5 w-5 text-brand shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h2 className="font-bold tracking-tight">These are real screenshots</h2>
            <p className="text-sm text-muted leading-relaxed max-w-2xl">
              Each one is captured by running the actual application and driving it with
              a browser, so a picture here cannot show a version of PROVIA that no longer
              exists. The data is made up and the phone numbers come from the range Ofcom
              reserves for fiction — no customer's information has ever been on this page.
            </p>
          </div>
        </div>
      </section>

      <section className="text-center space-y-5">
        <h2 className="text-title">Try it on your own portfolio</h2>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {canSignUp && (
            <Link to="/setup">
              <Button variant="primary" size="lg" icon={<ArrowRight className="h-4 w-4" />}>
                Start free trial
              </Button>
            </Link>
          )}
          <Link to="/pricing">
            <Button variant="outline" size="lg">See pricing</Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
